import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from '../calc-shared.module.scss';

interface ParseResult {
    valid: boolean;
    kind: 'date' | 'date-time' | 'time' | 'duration' | 'interval' | 'unknown';
    iso: string;
    epoch?: number;
    utc?: string;
    local?: string;
    parts?: Record<string, string | number>;
    error?: string;
}

// ISO 8601 regex patterns (RFC 3339 / Wikipedia spec).
const RX_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const RX_TIME = /^(\d{2}):(\d{2})(?::(\d{2}(?:\.\d+)?))?(Z|[+-]\d{2}:?\d{2})?$/;
const RX_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}(?:\.\d+)?))?(Z|[+-]\d{2}:?\d{2})?$/;
const RX_DURATION = /^P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;
const RX_INTERVAL = /^(.+)\/(.+)$/;

function isValidDateParts(y: number, mo: number, d: number): boolean {
    if (mo < 1 || mo > 12) return false;
    if (d < 1 || d > 31) return false;
    const date = new Date(Date.UTC(y, mo - 1, d));
    return date.getUTCFullYear() === y
        && date.getUTCMonth() === mo - 1
        && date.getUTCDate() === d;
}

function isValidTimeParts(h: number, mi: number, sec: number): boolean {
    if (h < 0 || h > 23) return false;
    if (mi < 0 || mi > 59) return false;
    if (sec < 0 || sec >= 60) return false;
    return true;
}

function parseIso(s: string): ParseResult {
    s = s.trim();
    if (!s) return { valid: false, kind: 'unknown', iso: s, error: 'empty' };
    // Try date-time
    let m = s.match(RX_DATETIME);
    if (m) {
        const y = +m[1], mo = +m[2], d = +m[3];
        const h = +m[4], mi = +m[5];
        const secStr = m[6] || '0';
        const sec = parseFloat(secStr);
        if (!isValidDateParts(y, mo, d)) {
            return { valid: false, kind: 'date-time', iso: s, error: `invalid calendar date ${y}-${m[2]}-${m[3]}` };
        }
        if (!isValidTimeParts(h, mi, sec)) {
            return { valid: false, kind: 'date-time', iso: s, error: `invalid time ${m[4]}:${m[5]}:${secStr}` };
        }
        const dt = new Date(s);
        if (isNaN(dt.getTime())) return { valid: false, kind: 'date-time', iso: s, error: 'unparseable date' };
        return {
            valid: true, kind: 'date-time', iso: s,
            epoch: Math.floor(dt.getTime() / 1000),
            utc: dt.toISOString(),
            local: dt.toLocaleString(),
            parts: {
                year: y, month: mo, day: d, hour: h, minute: mi,
                second: secStr, timezone: m[7] || '(local)',
            },
        };
    }
    // Try plain date
    m = s.match(RX_DATE);
    if (m) {
        const y = +m[1], mo = +m[2], d = +m[3];
        if (!isValidDateParts(y, mo, d)) {
            return { valid: false, kind: 'date', iso: s, error: `invalid calendar date ${y}-${m[2]}-${m[3]}` };
        }
        return {
            valid: true, kind: 'date', iso: s,
            parts: { year: y, month: mo, day: d },
        };
    }
    // Try plain time
    m = s.match(RX_TIME);
    if (m) {
        const h = +m[1], mi = +m[2];
        const secStr = m[3] || '0';
        const sec = parseFloat(secStr);
        if (!isValidTimeParts(h, mi, sec)) {
            return { valid: false, kind: 'time', iso: s, error: `invalid time ${m[1]}:${m[2]}:${secStr}` };
        }
        return {
            valid: true, kind: 'time', iso: s,
            parts: { hour: h, minute: mi, second: secStr, timezone: m[4] || '(local)' },
        };
    }
    // Try duration
    m = s.match(RX_DURATION);
    if (m) {
        if (s === 'P' || s === 'PT') return { valid: false, kind: 'duration', iso: s, error: 'duration must have at least one component' };
        return {
            valid: true, kind: 'duration', iso: s,
            parts: {
                years: +(m[1] || 0), months: +(m[2] || 0), weeks: +(m[3] || 0), days: +(m[4] || 0),
                hours: +(m[5] || 0), minutes: +(m[6] || 0), seconds: +(m[7] || 0),
            },
        };
    }
    // Try interval (start/end)
    m = s.match(RX_INTERVAL);
    if (m) {
        const a = parseIso(m[1]);
        const b = parseIso(m[2]);
        if (a.valid && b.valid) {
            return { valid: true, kind: 'interval', iso: s, parts: { from: m[1], to: m[2] } };
        }
        return { valid: false, kind: 'interval', iso: s, error: 'interval components must each be valid ISO 8601' };
    }
    return { valid: false, kind: 'unknown', iso: s, error: 'does not match any ISO 8601 form' };
}

interface State {
    input: string;
    result: ParseResult;
}

export default class Iso8601ValidatorComponent extends Component<{ feature?: string }, State> {
    state: State = (() => {
        const sample = new Date().toISOString();
        return { input: sample, result: parseIso(sample) };
    })();

    onChange = (v: string) => {
        this.setState({ input: v, result: parseIso(v) });
    };

    render() {
        const { input, result } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>
                        ISO 8601 input
                        <input className={styles.input} type="text" value={input} spellCheck={false}
                            onChange={e => this.onChange(e.target.value)} />
                    </label>
                    <p className={styles.note}>
                        Supports date (YYYY-MM-DD), time (hh:mm[:ss][Z|±hh:mm]), date-time, duration (P…),
                        and interval (date1/date2 or date1/duration).
                    </p>
                    <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {[
                            '2026-05-11',
                            '2026-05-11T13:30:00Z',
                            '13:30:00+02:00',
                            'P1Y2M3DT4H5M6S',
                            'PT15M',
                            '2026-01-01/2026-12-31',
                            '2026-01-01/P3M',
                        ].map(ex => (
                            <button key={ex} className={styles.unitBtn} onClick={() => this.onChange(ex)} style={{ flex: '0 0 auto', padding: '0.35rem 0.65rem', fontSize: '0.78rem' }}>{ex}</button>
                        ))}
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>Result</label>
                    <div className={styles.resultBox}>
                        <div className={styles.resultLabel}>Validation</div>
                        <div className={styles.resultValue} style={{ fontSize: '1.4rem' }}>
                            {result.valid ? `✓ Valid ${result.kind}` : `✗ Invalid`}
                        </div>
                        {result.error && <div className={styles.resultSecondary}>{result.error}</div>}
                    </div>
                    {result.parts && (
                        <div className={styles.kvBox}>
                            {Object.entries(result.parts).map(([k, v]) => (
                                <div key={k} className={styles.kv}><span>{k}</span><span>{String(v)}</span></div>
                            ))}
                            {result.utc && <div className={styles.kv}><span>UTC</span><span>{result.utc}</span></div>}
                            {result.local && <div className={styles.kv}><span>Local</span><span>{result.local}</span></div>}
                            {result.epoch !== undefined && <div className={styles.kv}><span>Unix epoch</span><span>{result.epoch}</span></div>}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
